@file:OptIn(ExperimentalFoundationApi::class, kotlin.io.encoding.ExperimentalEncodingApi::class)

package com.example.monetix.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.combinedClickable
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.layout.ContentScale
import androidx.compose.foundation.basicMarquee
import kotlin.io.encoding.Base64

import coil3.compose.AsyncImage
import coil3.compose.rememberAsyncImagePainter
import kotlinx.datetime.LocalDate

import com.example.monetix.db.TransactionEntity
import com.example.monetix.stripSystemMarker
import com.example.monetix.utils.formatEuroFromCents

fun getSafeWebImageUrl(originalUrl: String?): String? {
    if (originalUrl.isNullOrBlank()) return null
    if (originalUrl.contains("wsrv.nl") || originalUrl.contains("icon.horse") || originalUrl.contains("ui-avatars.com")) {
        return originalUrl
    }
    val cleanUrl = originalUrl.replace("https://", "").replace("http://", "")
    return "https://wsrv.nl/?url=$cleanUrl"
}

@Composable
fun TxLogo(
    logoEmoji: String?,
    logoUri: String?,
    name: String?,
    modifier: Modifier = Modifier.size(40.dp)
) {
    val rawName = stripSystemMarker(name ?: "")?.trim() ?: ""
    val cleanName = if (rawName.isBlank()) "Movimento" else rawName

    val domain = cleanName.lowercase().replace(" ", "") + ".com"
    val automaticLogoUrl = "https://icon.horse/icon/$domain"
    val fallbackUrl = "https://ui-avatars.com/api/?name=$cleanName&background=random&color=fff&size=128"

    val validEmoji = logoEmoji?.trim()?.takeIf {
        it.isNotBlank() && it != "null" && it != "🔄" && it != "None"
    }

    val rawUri = logoUri?.trim()?.takeIf { it.isNotBlank() && it != "null" }

    val validUri = rawUri?.let { uri ->
        if (uri.startsWith("http") && !uri.contains("wsrv.nl") && !uri.contains("icon.horse") && !uri.contains("ui-avatars.com")) {
            getSafeWebImageUrl(uri)
        } else {
            uri
        }
    }

    val imageModel = remember(validUri) {
        if (validUri != null && validUri.startsWith("data:image")) {
            try {
                val base64String = validUri.substringAfter("base64,")
                Base64.Default.decode(base64String)
            } catch (e: Exception) {
                validUri
            }
        } else {
            validUri
        }
    }

    Box(
        modifier = modifier
            .clip(CircleShape)
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)),
        contentAlignment = Alignment.Center
    ) {
        when {
            imageModel != null -> {
                AsyncImage(
                    model = imageModel,
                    contentDescription = cleanName,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.fillMaxSize(),
                    error = rememberAsyncImagePainter(fallbackUrl)
                )
            }
            validEmoji != null -> {
                Text(text = validEmoji, fontSize = 20.sp)
            }
            else -> {
                AsyncImage(
                    model = automaticLogoUrl,
                    contentDescription = cleanName,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxSize().padding(4.dp),
                    error = rememberAsyncImagePainter(fallbackUrl)
                )
            }
        }
    }
}

@Composable
fun LogoBadge(
    logoEmoji: String?,
    logoUri: String?,
    name: String?,
    sizeDp: Int = 40
) {
    Surface(
        modifier = Modifier.size(sizeDp.dp),
        shape = CircleShape,
        color = MaterialTheme.colorScheme.primaryContainer
    ) {
        Box(contentAlignment = Alignment.Center) {
            TxLogo(logoEmoji, logoUri, name, modifier = Modifier.size((sizeDp * 0.8).dp))
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun TransactionRow(
    tx: TransactionEntity,
    onTap: () -> Unit,
    onLongPressAddFavorite: () -> Unit,
    categoryNameById: Map<String, String>,
    accountNameById: Map<String, String>,
    hideAmounts: Boolean
) {
    val isIncome = tx.type == "INCOME"
    val isTransfer = tx.type == "TRANSFER"
    val sign = if (isIncome) "+" else if (isTransfer) "" else "-"
    val amountColor = when {
        isIncome -> Color(0xFF10B981)   // Verde
        isTransfer -> Color(0xFF60A5FA) // Celeste
        else -> Color.White             // Bianco
    }

    val date = LocalDate.fromEpochDays(tx.dateEpochDay.toInt())
    val dateFormatted = "${date.dayOfMonth.toString().padStart(2, '0')}/${date.monthNumber.toString().padStart(2, '0')}"

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .combinedClickable(onClick = onTap, onLongClick = onLongPressAddFavorite)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TxLogo(
            logoEmoji = tx.logoEmoji,
            logoUri = tx.logoUri,
            name = tx.merchantKey ?: tx.description
        )

        Spacer(Modifier.width(12.dp))

        Column(modifier = Modifier.weight(1f)) {
            val rawMerchant = tx.merchantKey?.let { stripSystemMarker(it) }
            val rawDesc = tx.description?.let { stripSystemMarker(it) }
            val displayName = rawMerchant ?: rawDesc ?: "Operazione"

            Text(
                text = displayName.uppercase(),
                modifier = Modifier.basicMarquee(),
                style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold),
                color = Color.White,
                maxLines = 1
            )
            val catName = categoryNameById[tx.categoryId] ?: "Altro"
            Text(
                text = "$dateFormatted • $catName",
                style = MaterialTheme.typography.bodySmall,
                color = Color(0xFFA0A0A0)
            )
        }

        // ==========================================
        // 🚀 FORMATTAZIONE MODERNA GLOBALE APPLICATA
        // ==========================================
        ModernMoneyText(
            cents = tx.amountCents,
            hideBalances = hideAmounts,
            sign = sign, // Usa il segno calcolato sopra
            color = amountColor,
            integerFontSize = 18.sp,
            decimalFontSize = 12.sp,
            symbolFontSize = 12.sp
        )
    }
}